import { createColumnHelper } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle } from "@fortawesome/free-solid-svg-icons";
import { LogLevel, LogLevelStyles, Permissions } from "../../app/enums";
import { ITopMessage } from "../../models/TopMessages";
import { usePermissionCheck } from "../../app/common/helper/Permissions";
import { NavLink } from "react-router-dom";
import SimpleTable from "../../components/simpletable";
import { formatTopMessageLastSeen } from "../../app/common/helper/TopMessageLastSeen";

interface ITopMessagesTableRow extends ITopMessage {
  searchableText: string;
}

export interface IHighestMessagesProps {
  data: ITopMessage[];
  isLoading: boolean;
}

export default function HighestMessages(props: IHighestMessagesProps) {
  const columnHelper = createColumnHelper<ITopMessagesTableRow>();
  const now = new Date();

  let CanViewEstablishments: boolean = usePermissionCheck(Permissions.CanViewEstablishments);

  const columns = [
    columnHelper.display({
      id: 'rowNumber',
      header: () => <span style={{ width: '15px', display: 'block' }}>#</span>,
      size: 1,
      minSize: 1,
      maxSize: 1,
      cell: ({ row }) => (
        <div className="short-text" style={{ width: '15px' }}>
          <span>{row.index + 1}</span>
        </div>
      ),
    }),
    columnHelper.accessor('establishmentName', {
      header: () => <span>Establishment</span>,
      size: 15,
      minSize: 15,
      maxSize: 15,
      cell: ({ row }) => (
        <div className="long-text" title={row.original.establishmentName as string}>
          {
            CanViewEstablishments ?
              <NavLink to={`/establishment/${row.original.establishmentID}`}>
                {row.original.establishmentName}
              </NavLink>
              :
              row.original.establishmentName
          }
        </div>
      ),
    }),
    columnHelper.accessor('message', {
      header: () => <span>Message</span>,
      size: 20,
      minSize: 20,
      maxSize: 20,
      cell: ({ row }) => (
        <div className="long-text" title={row.original.message as string}>
          <span>{row.original.message}</span>
        </div>
      ),
    }),
    columnHelper.accessor('instanceCount', {
      header: () => <span>Instance Count</span>,
      size: 12,
      minSize: 12,
      maxSize: 12,
      cell: ({ row }) => (
        <div className="short-text">
          <span>{row.original.instanceCount}</span>
        </div>
      ),
    }),
    columnHelper.accessor('daysSeen', {
      header: () => <span>Last Seen</span>,
      size: 12,
      minSize: 12,
      maxSize: 12,
      cell: ({ row }) => (
        <div className="short-text">
          <span>{formatTopMessageLastSeen(row.original, now)}</span>
        </div>
      ),
    }),
    columnHelper.accessor('deviceName', {
      header: () => <span>Device</span>,
      size: 12,
      minSize: 12,
      maxSize: 12,
      cell: ({ row }) => (
        <div className="short-text" title={row.original.deviceName as string}>
          <span>{row.original.deviceName}</span>
        </div>
      ),
    }),
    columnHelper.accessor('productName', {
      header: () => <span>Product</span>,
      size: 12,
      minSize: 12,
      maxSize: 12,
      cell: ({ row }) => (
        <div className="short-text" title={row.original.productName as string}>
          <span>{row.original.productName}</span>
        </div>
      ),
    }),
    columnHelper.accessor('logLevel', {
      header: () => <span>Log Level</span>,
      size: 12,
      minSize: 12,
      maxSize: 12,
      cell: ({ row }) => (
        <div className="loglevel-box">
          <div className={`p-1 ps-2 pe-2 logs ${LogLevelStyles[row.original.logLevel]}`}>
            <FontAwesomeIcon icon={faCircle} size="2xs"
              className={`${LogLevelStyles[row.original.logLevel]}`} />
            <span className="ms-2">{LogLevel[row.original.logLevel]}</span>
          </div>
        </div>
      ),
    })
  ];

  return (
    <div className="top-messages-table">
      <SimpleTable
        tableKey={"highest-messages-table"}
        columns={columns}
        data={props.data}
        isLoading={props.isLoading}
      />
    </div>
  );
}
